import React, { useEffect, useRef, useState } from 'react';
import { useOktaAuth } from '@okta/okta-react';
import LoginPage from './LoginPage';
import AuthLoading from './AuthLoading';
import { authService } from '../services/authService';
import { sanitizeReturnTo } from '../utils/returnTo';

// How long, after login, we'll keep showing the loading spinner instead of the
// login screen while Okta's authState settles. Right after the PKCE callback —
// and again whenever the app is reopened with an existing session — authState
// momentarily reads `isAuthenticated: false` before the freshly-stored/renewed
// tokens propagate. Rendering <LoginPage /> during that blip is what caused the
// "loads, then loads again" flash users reported. We suppress it for this
// bounded window, then fall back to the login screen if auth truly never
// resolves (e.g. expired session, failed renewal).
const AUTH_SETTLE_GRACE_MS = 2500;

// Once the app is on-screen, Okta keeps re-emitting authState as it validates
// and renews tokens. Those updates briefly report `isAuthenticated: false`,
// which — if we reacted immediately — would tear the rendered app back down to
// the loading screen and flash it. Instead we wait this long: if auth is STILL
// unauthenticated after the window, it's a real logout/expiry and we show the
// login screen; otherwise it was just a transient blip and the app never moved.
const AUTH_LOGOUT_CONFIRM_MS = 4000;

// Synchronous best-guess of whether auth is still "settling" (so we should show
// the loader) vs. genuinely absent (so we should show the login screen). Used
// to seed the settling state and to avoid a loader flash before the async probe
// resolves. Returns 'yes' when we're in the post-login window or tokens already
// exist in storage; 'no' otherwise.
const guessSettling = (oktaAuth) => {
  try {
    const startedAt = Number(sessionStorage.getItem('lyric.authSettling')) || 0;
    if (startedAt && Date.now() - startedAt < AUTH_SETTLE_GRACE_MS) return 'yes';
  } catch { /* ignore */ }
  try {
    const t = oktaAuth?.tokenManager?.getTokensSync?.();
    if (t && (t.accessToken || t.idToken)) return 'yes';
  } catch { /* ignore */ }
  return 'no';
};

// Is there a deployed-app destination stashed for us to return to once we're
// signed in again (query param on first hit, or sessionStorage after LoginPage
// stashes it)? Used to tell "the user was bounced here from /apps/<name>/…"
// apart from "the user deliberately opened Lyric's own URL" — only the former
// should skip straight past our branded login page into the Okta redirect.
const getPendingReturnTo = () => {
  try {
    const params = new URLSearchParams(window.location.search);
    return (
      sanitizeReturnTo(params.get('return_to')) ||
      sanitizeReturnTo(sessionStorage.getItem('lyric.returnTo')) ||
      null
    );
  } catch {
    return null;
  }
};

const SecureRoute = ({ children }) => {
  const { authState, oktaAuth } = useOktaAuth();
  const [verificationStatus, setVerificationStatus] = useState('checking');
  // 'yes' -> auth likely still settling (show spinner, not LoginPage);
  // 'no' -> genuinely logged out (show LoginPage). Seed synchronously so a user
  // with no stored tokens (e.g. right after sign-out, or a first-time visitor)
  // goes straight to <LoginPage /> instead of flashing the "Signing you in…"
  // loader for a render while the async settling probe runs.
  const [settling, setSettling] = useState(() => guessSettling(oktaAuth));
  // Latches true once the app has been successfully verified and rendered.
  // From then on we keep the app mounted through Okta's transient authState
  // blips and only return to the login screen on a confirmed, sustained logout.
  const authedOnce = useRef(false);
  // Guards the *backend* verification (/api/verify) so it runs exactly once per
  // authenticated session — that call is idempotent and doesn't need repeating.
  const verifiedRef = useRef(false);
  // The access-token string last used to (re)establish the server-side session.
  // The /apps/* proxy forwards the Okta token stored at establish time and
  // rejects deployed-app requests once that token's `exp` passes (showing the
  // app's "session expired" screen). Okta autoRenew emits a fresh token
  // periodically, so we MUST re-establish whenever the token changes to keep the
  // forwarded token current. Tracking the token value here lets us re-establish
  // only on real renewals — not on every authState emission — so we refresh the
  // session without spamming the endpoint.
  const establishedTokenRef = useRef(null);
  const [confirmedLoggedOut, setConfirmedLoggedOut] = useState(false);
  // Guards the auto-redirect-to-Okta effect below so it fires at most once per
  // mount, even as authState/settling re-emit several times while we wait.
  const autoLoginTriedRef = useRef(false);
  // Flips true if signInWithRedirect() itself rejects (e.g. Okta init/
  // transaction failure), so the render below falls back to the branded
  // login page instead of leaving the user on an endless spinner with no
  // way to retry.
  const [autoLoginFailed, setAutoLoginFailed] = useState(false);

  // A logged-out user re-opening a deployed app (/apps/<name>/…) gets bounced
  // here with return_to stashed. Making them see OUR branded login page and
  // click "Sign in with OKTA SSO" before continuing to Okta is an extra,
  // jarring flash for what should be a transparent pass-through re-auth —
  // especially when Okta's own SSO session is still alive and the redirect
  // would complete almost instantly anyway. So: when we're about to show the
  // login screen AND we know where the user was headed, skip our own page and
  // go straight to Okta. If Okta's session is gone too, they land on Okta's
  // real sign-in form same as before — we've only removed OUR interstitial.
  useEffect(() => {
    if (autoLoginTriedRef.current) return;
    if (autoLoginFailed) return;
    if (authedOnce.current) return; // app already on-screen; not a login case

    let justLoggedOut = false;
    try { justLoggedOut = sessionStorage.getItem('lyric.justLoggedOut') === '1'; } catch { /* ignore */ }
    const genuinelyUnauthenticated =
      (authState && !authState.isAuthenticated && settling === 'no') ||
      (justLoggedOut && !(authState && authState.isAuthenticated && verificationStatus === 'verified'));
    if (!genuinelyUnauthenticated) return;

    const pendingReturnTo = getPendingReturnTo();
    if (!pendingReturnTo) return; // deliberate top-level visit — show the real login page

    autoLoginTriedRef.current = true;
    try { sessionStorage.setItem('lyric.returnTo', pendingReturnTo); } catch { /* ignore */ }
    // Deliberately do NOT clear 'lyric.justLoggedOut' here. The verifyAuth
    // effect below relies on that latch to refuse re-establishing a session
    // from a stale authState.isAuthenticated=true while this redirect is in
    // flight — clearing it early would let a stale token recreate the server
    // session out from under the logout. LoginCallback already clears the
    // latch once we land back there post-redirect, so it doesn't need to
    // happen here too.
    Promise.resolve().then(() => oktaAuth.signInWithRedirect()).catch((err) => {
      console.error('Auto sign-in redirect failed, falling back to login page:', err);
      setAutoLoginFailed(true);
    });
  }, [authState, settling, verificationStatus, oktaAuth, autoLoginFailed]);

  useEffect(() => {
    const verifyAuth = async () => {
      if (authState && authState.isAuthenticated) {
        // Hard stop: if the user just clicked sign out, refuse to verify or
        // re-establish a session. Without this, the bounce-back from Okta
        // /logout sees authState.isAuthenticated still briefly true (stale
        // localStorage tokens), runs through this block, and silently
        // creates a brand new session cookie that gates the deployed apps
        // back open. Force a true logout instead.
        let justLoggedOut = false;
        try { justLoggedOut = sessionStorage.getItem('lyric.justLoggedOut') === '1'; } catch { /* ignore */ }
        if (justLoggedOut) {
          console.log('Just logged out — skipping verify/establish and forcing full sign-out');
          try { sessionStorage.removeItem('lyric.justLoggedOut'); } catch { /* ignore */ }
          try { await authService.destroySession(); } catch { /* ignore */ }
          try { await oktaAuth.tokenManager.clear(); } catch { /* ignore */ }
          authedOnce.current = false;
          verifiedRef.current = false;
          establishedTokenRef.current = null;
          setVerificationStatus('checking'); // will show LoginPage on next render
          return;
        }

        // Authentication has resolved — the post-login settling window is over,
        // so drop the flag that suppresses the login screen during that window.
        try { sessionStorage.removeItem('lyric.authSettling'); } catch { /* ignore */ }

        // Keep the server-side session's forwarded Okta token fresh. The
        // /apps/<name>/* proxy snapshots the access token at establish time and
        // rejects requests once its `exp` passes — which is what made deployed
        // apps show "session expired". Okta autoRenew emits a renewed token
        // before expiry; re-establish whenever the token value changes so the
        // proxy always forwards a live token. Guarded by the token value (not a
        // run-once flag) so repeated authState emissions with the SAME token
        // don't re-hit the endpoint, while genuine renewals always refresh it.
        const currentToken = authState.accessToken?.accessToken || null;
        const needEstablish = currentToken
          ? currentToken !== establishedTokenRef.current
          : !establishedTokenRef.current;
        if (needEstablish) {
          try {
            await authService.establishSession();
            establishedTokenRef.current = currentToken || 'established';
            console.log('Session cookie established/refreshed');
          } catch (sessErr) {
            console.warn('Session establish failed (deployed apps may require re-login):', sessErr.message);
          }
        }

        // Verify with the backend only once per authenticated session. That
        // call (/api/verify) is idempotent, so there's no need to repeat it on
        // every autoRenew emission. (The session cookie, above, IS refreshed on
        // renewal — the two concerns are intentionally decoupled.)
        if (verifiedRef.current) {
          return;
        }

        try {
          console.log('Verifying authentication with backend...');
          await authService.verifyAuthentication();
          console.log('Authentication verified successfully');

          // Honor return_to=/apps/<name>/... — sent by the proxy when a
          // logged-out user hits a deployed app URL directly. It arrives as a
          // ?return_to= query param, but that query string does NOT survive the
          // Okta PKCE round-trip, so LoginPage stashes it in sessionStorage
          // before redirecting. Read the URL first (session-still-alive case),
          // then fall back to the stash (post-Okta-login case). We reach this
          // point only AFTER establishSession() above has set the session
          // cookie, so the hard navigation below will pass the proxy's gate
          // instead of bouncing back to login. We still sanitize defensively
          // before navigating.
          try {
            const params = new URLSearchParams(window.location.search);
            let raw = params.get('return_to');
            if (!raw) {
              try { raw = sessionStorage.getItem('lyric.returnTo'); } catch { /* ignore */ }
            }
            // Reject dot-segment escapes / encoded variants / schemes so
            // window.location.replace() can't normalize the target to a path
            // outside the /apps/ mount (e.g. /apps/foo/../../admin -> /admin).
            const safe = sanitizeReturnTo(raw);
            if (safe) {
              try { sessionStorage.removeItem('lyric.returnTo'); } catch { /* ignore */ }
              console.log(`Redirecting to original destination: ${safe}`);
              window.location.replace(safe);
              return; // unmount-pending — don't flip state
            }
          } catch { /* ignore — URL parsing should never throw, but be safe */ }

          verifiedRef.current = true;
          authedOnce.current = true;
          setConfirmedLoggedOut(false);
          setVerificationStatus('verified');
        } catch (error) {
          console.error('Backend authentication verification failed:', error);

          // Check if it's a network error (backend might be down)
          if (error.message.includes('Network connection issue')) {
            console.warn('Backend verification failed due to network issues, proceeding with frontend auth only');
            verifiedRef.current = true;
            authedOnce.current = true;
            setConfirmedLoggedOut(false);
            setVerificationStatus('verified'); // Allow access with frontend auth only
          } else {
            setVerificationStatus('failed');
          }
        }
      } else if (authState && !authState.isAuthenticated) {
        // Clear any failed status when user is not authenticated
        setVerificationStatus('checking');
      }
    };

    if (authState) {
      verifyAuth();
    }
  }, [authState]);

  // Decide whether an *unauthenticated* authState is a genuine logged-out state
  // or just the brief settling window right after login / app reopen. Only in
  // the genuine case do we show <LoginPage />; otherwise we keep the loader up
  // so the login screen never flashes on-screen mid-transition.
  useEffect(() => {
    let cancelled = false;
    let timer;

    const decide = async () => {
      if (!(authState && !authState.isAuthenticated)) {
        // authState is null (SDK initializing) or authenticated — neither
        // renders via the settling branch. Seed a synchronous best-guess
        // (not a bare 'unknown') so that the instant authState resolves to
        // unauthenticated we already show the right screen with no loader flash.
        setSettling(guessSettling(oktaAuth));
        return;
      }

      let stillSettling = false;

      // Signal 1: we just came back from the Okta callback (flag set by
      // LoginCallback) and are within the grace window.
      try {
        const startedAt = Number(sessionStorage.getItem('lyric.authSettling')) || 0;
        if (startedAt && Date.now() - startedAt < AUTH_SETTLE_GRACE_MS) stillSettling = true;
      } catch { /* ignore */ }

      // Signal 2: tokens already exist in storage (e.g. reopening the app with a
      // live session) but authState hasn't renewed to authenticated yet.
      if (!stillSettling) {
        try {
          const tokens = await oktaAuth.tokenManager.getTokens();
          if (tokens && (tokens.accessToken || tokens.idToken)) stillSettling = true;
        } catch { /* ignore */ }
      }

      if (cancelled) return;

      if (stillSettling) {
        setSettling('yes');
        // Bounded fallback: if auth never resolves, stop hiding the login screen.
        timer = setTimeout(() => { if (!cancelled) setSettling('no'); }, AUTH_SETTLE_GRACE_MS);
      } else {
        setSettling('no');
      }
    };

    decide();
    return () => { cancelled = true; if (timer) clearTimeout(timer); };
  }, [authState, oktaAuth]);

  // Once the app has rendered (authedOnce), an unauthenticated authState is
  // almost always a transient token-settling blip — reacting to it is exactly
  // what flashed the loader back over the app. Debounce it: only confirm a real
  // logout after the state stays unauthenticated past the window (or on an
  // explicit sign-out). If auth flips back before then, we never moved.
  useEffect(() => {
    if (!authedOnce.current) return undefined;

    if (authState && authState.isAuthenticated) {
      setConfirmedLoggedOut(false);
      return undefined;
    }

    // authState is null or unauthenticated here.
    let justLoggedOut = false;
    try { justLoggedOut = sessionStorage.getItem('lyric.justLoggedOut') === '1'; } catch { /* ignore */ }
    if (justLoggedOut) {
      authedOnce.current = false;
      verifiedRef.current = false;
      establishedTokenRef.current = null;
      setConfirmedLoggedOut(true);
      return undefined;
    }

    const timer = setTimeout(() => {
      authedOnce.current = false;
      verifiedRef.current = false;
      establishedTokenRef.current = null;
      setConfirmedLoggedOut(true);
    }, AUTH_LOGOUT_CONFIRM_MS);
    return () => clearTimeout(timer);
  }, [authState]);

  // If the user just clicked "Sign out", show the login screen immediately.
  // Logout round-trips through Okta and reloads the app at the origin; on that
  // reload authState is briefly null/settling, and the generic "Signing you
  // in…" loader would otherwise flash on-screen (with the wrong message)
  // before the login page appears — the "loads, then loads again" flash. The
  // justLoggedOut latch is the definitive "logging out" signal, so short-
  // circuit straight to <LoginPage /> and skip the loader detour. The
  // verifyAuth effect still runs in the background to tear down any lingering
  // session/tokens if authState momentarily reads authenticated.
  let justLoggedOut = false;
  try { justLoggedOut = sessionStorage.getItem('lyric.justLoggedOut') === '1'; } catch { /* ignore */ }
  if (justLoggedOut && !(authState && authState.isAuthenticated && verificationStatus === 'verified')) {
    // A pending return_to means the effect above is about to auto-redirect to
    // Okta — show the spinner instead of flashing our own login page first.
    // (Skip this if that redirect already failed — fall back to the real
    // login page instead of an endless spinner.)
    if (getPendingReturnTo() && !autoLoginFailed) {
      return <AuthLoading message="Signing you in…" />;
    }
    return <LoginPage />;
  }

  // The app has been shown at least once and we haven't confirmed a logout:
  // keep it mounted straight through Okta's transient authState churn so the
  // loading screen never flashes back over it.
  if (authedOnce.current && !confirmedLoggedOut) {
    return children;
  }

  if (!authState) {
    return <AuthLoading message="Signing you in…" />;
  }

  if (!authState.isAuthenticated) {
    // Suppress the login screen while auth is still settling to avoid the flash.
    if (settling === 'unknown' || settling === 'yes') {
      return <AuthLoading message="Signing you in…" />;
    }
    try { sessionStorage.removeItem('lyric.authSettling'); } catch { /* ignore */ }
    // A pending return_to means the effect above is about to auto-redirect to
    // Okta — show the spinner instead of flashing our own login page first.
    // (Skip this if that redirect already failed — fall back to the real
    // login page instead of an endless spinner.)
    if (getPendingReturnTo() && !autoLoginFailed) {
      return <AuthLoading message="Signing you in…" />;
    }
    return <LoginPage />;
  }

  if (verificationStatus === 'checking') {
    return <AuthLoading message="Signing you in…" />;
  }

  if (verificationStatus === 'failed') {
    const handleRetry = async () => {
      try {
        // Clear the current session and start fresh
        await oktaAuth.signOut();
        // Reset verification status to allow fresh login
        setVerificationStatus('checking');
        // Redirect to login
        window.location.href = '/';
      } catch (error) {
        console.error('Error during logout:', error);
        // Fallback: just redirect to home
        window.location.href = '/';
      }
    };

    return (
      <div style={styles.loadingContainer}>
        <div style={styles.errorContainer}>
          <h2 style={styles.errorTitle}>Authentication Issue</h2>
          <p style={styles.errorMessage}>
            We couldn't verify your authentication. This might be due to a session timeout or network issue.
          </p>
          <div style={styles.buttonContainer}>
            <button onClick={handleRetry} style={styles.primaryButton}>
              Return to Login
            </button>
            <button onClick={() => window.location.reload()} style={styles.secondaryButton}>
              Refresh Page
            </button>
          </div>
        </div>
      </div>
    );
  }

  return children;
};

const styles = {
  loadingContainer: {
    minHeight: '100vh',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    color: '#ffffff',
    padding: '2rem',
  },
  spinner: {
    width: '40px',
    height: '40px',
    border: '4px solid rgba(255, 255, 255, 0.3)',
    borderTop: '4px solid #ffffff',
    borderRadius: '50%',
    animation: 'spin 1s linear infinite',
    marginBottom: '1rem',
  },
  errorContainer: {
    backgroundColor: '#ffffff',
    borderRadius: '12px',
    padding: '3rem',
    textAlign: 'center',
    boxShadow: '0 20px 40px rgba(0, 0, 0, 0.1)',
    maxWidth: '500px',
    color: '#333',
  },
  errorTitle: {
    fontSize: '1.8rem',
    color: '#dc3545',
    marginBottom: '1rem',
    fontWeight: 'bold',
  },
  errorMessage: {
    fontSize: '1rem',
    color: '#666',
    lineHeight: 1.6,
    marginBottom: '2rem',
  },
  buttonContainer: {
    display: 'flex',
    gap: '1rem',
    justifyContent: 'center',
    flexWrap: 'wrap',
  },
  primaryButton: {
    backgroundColor: '#007bff',
    color: '#ffffff',
    border: 'none',
    borderRadius: '8px',
    padding: '0.75rem 1.5rem',
    fontSize: '1rem',
    fontWeight: 'bold',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
  },
  secondaryButton: {
    backgroundColor: '#6c757d',
    color: '#ffffff',
    border: 'none',
    borderRadius: '8px',
    padding: '0.75rem 1.5rem',
    fontSize: '1rem',
    fontWeight: 'bold',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
  },
};

export default SecureRoute;
